import type { ProductListItem, WrapperType } from "@repo/shared/types";
import type { Localize } from "@/lib/i18n/use-localize";

/**
 * A product as an option of the searchable product picker. The ISIN is a
 * keyword as well as part of the label, so a code that is truncated on screen
 * still matches; a product without one shows its name alone.
 */
export function productOption(
  product: Pick<ProductListItem, "id" | "nameI18n" | "isin">,
  localize: Localize,
): { value: string; label: string; keywords: string[] } {
  const name = localize(product.nameI18n);
  return {
    value: product.id,
    label: product.isin ? `${name} (${product.isin})` : name,
    keywords: product.isin ? [product.isin] : [],
  };
}

/**
 * Names of a product's wrapper types, in the product's preference order
 * (order 0 = the wrapper used when accounts are created at flow completion).
 * A wrapper type that is gone is left out.
 */
export function wrapperNamesOf(
  product: Pick<ProductListItem, "wrapperTypeIds"> | undefined,
  wrapperTypes: Pick<WrapperType, "id" | "nameI18n">[],
  localize: Localize,
): string[] {
  return (product?.wrapperTypeIds ?? [])
    .map((wrapperTypeId) => wrapperTypes.find((wt) => wt.id === wrapperTypeId))
    .filter((wt) => wt !== undefined)
    .map((wt) => localize(wt.nameI18n));
}
